import { useState, useCallback, useRef } from 'react'
import { Button } from '@/components/ui/button'
import {
  Paperclip,
  Plus,
  Trash2,
  Loader2,
  FileText,
  Image as ImageIcon,
  File,
  Download,
  Eye,
} from 'lucide-react'
import {
  useDocumentosEntidad,
  useSubirDocumento,
  useVincularDocumento,
  useDesvincularDocumento,
  useDescargarDocumento,
  useObtenerUrlDocumento,
  LIMITE_ARCHIVO_MB,
  formatFileSize,
  esArchivoPermitido,
  esTamanoPermitido,
} from '@/core/hooks/useDocumentos'
import { useNotificacionesTarea } from '@/core/hooks/useNotificacionesTarea'
import { obtenerTareaParaNotificacion, useTarea } from '@/core/hooks/useTareas'
import { useQueryClient } from '@tanstack/react-query'
import { useSupabase } from '@/lib/supabase-context'
import type { Documento } from '@/types/database'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'
import { useAuth0 } from '@auth0/auth0-react'
import { usePermissions } from '@/hooks/usePermissions'

interface TareaAdjuntosProps {
  tareaId: string
  clienteId?: string | null
  className?: string
}

function getDocumentoIcon(mimeType: string | null) {
  if (!mimeType) return <File className="h-4 w-4 text-gray-400" />
  if (mimeType.startsWith('image/')) return <ImageIcon className="h-4 w-4 text-blue-500" />
  if (mimeType === 'application/pdf') return <FileText className="h-4 w-4 text-red-500" />
  return <File className="h-4 w-4 text-gray-400" />
}

export function TareaAdjuntos({ tareaId, clienteId, className }: TareaAdjuntosProps) {
  const { user } = useAuth0()
  const { puede, esContador } = usePermissions()
  const { data: tarea } = useTarea(tareaId)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isUploading, setIsUploading] = useState(false)
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { notificarActividad } = useNotificacionesTarea()

  // Queries y mutations
  const { data: documentos = [], isLoading, refetch } = useDocumentosEntidad('tarea', tareaId)
  const subirDocumento = useSubirDocumento()
  const vincularDocumento = useVincularDocumento()
  const desvincularDocumento = useDesvincularDocumento()
  const descargarDocumento = useDescargarDocumento()
  const obtenerUrl = useObtenerUrlDocumento()

  // Mismas reglas que las policies de `documentos` y `documento_vinculaciones`. En una tarea sin
  // cliente sube el admin de la organización o quien creó o tiene asignada la tarea. Ese usuario
  // quita solo lo que subió él.
  const email = user?.email
  const esDeLaTarea = !!email && (tarea?.asignado_a === email || tarea?.created_by === email)
  const puedeSubir = puede.subirDocumento && (!!clienteId || esContador || esDeLaTarea)
  const puedeQuitar = useCallback(
    (documento: Documento) =>
      puede.eliminarDocumento || (esDeLaTarea && documento.created_by === email),
    [puede.eliminarDocumento, esDeLaTarea, email]
  )

  const handleClickSubir = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return

      // Validaciones
      if (!esArchivoPermitido(file)) {
        toast.error('Tipo de archivo no permitido')
        return
      }

      if (!esTamanoPermitido(file)) {
        toast.error(`El archivo excede el limite de ${LIMITE_ARCHIVO_MB}MB`)
        return
      }

      setIsUploading(true)

      try {
        // 1. Subir documento
        const documento = await subirDocumento.mutateAsync({
          file,
          clienteId: clienteId || '',
          createdBy: user?.email || '',
          metadatos: {
            nombre: file.name,
            categoria: 'otro',
          },
        })

        // 2. Vincular documento a la tarea
        await vincularDocumento.mutateAsync({
          documentoId: documento.id,
          entidadTipo: 'tarea',
          entidadId: tareaId,
          rol: 'adjunto',
        })

        refetch()
        toast.success('Archivo adjuntado')

        // 3. Notificar al asignado y/o supervisor de la tarea (best-effort)
        if (supabase) {
          const tarea = await obtenerTareaParaNotificacion(queryClient, supabase, tareaId)
          if (tarea) {
            notificarActividad(tarea, { tipo: 'adjunto', nombreArchivo: file.name })
          }
        }
      } catch (error) {
        console.error('Error al subir archivo:', error)
        toast.error('Error al subir archivo')
      } finally {
        setIsUploading(false)
        // Limpiar input
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
      }
    },
    [clienteId, user?.email, subirDocumento, vincularDocumento, tareaId, refetch, queryClient, notificarActividad, supabase]
  )

  const handleDescargar = useCallback(
    async (documento: Documento) => {
      try {
        await descargarDocumento.mutateAsync({
          path: documento.archivo_path,
          nombreArchivo: documento.nombre_original,
        })
        toast.success('Descarga iniciada')
      } catch (error) {
        console.error('Error al descargar:', error)
        toast.error('Error al descargar')
      }
    },
    [descargarDocumento]
  )

  const handleVer = useCallback(
    async (documento: Documento) => {
      try {
        const url = await obtenerUrl.mutateAsync(documento.archivo_path)
        window.open(url, '_blank')
      } catch (error) {
        console.error('Error al obtener URL:', error)
        toast.error('Error al abrir archivo')
      }
    },
    [obtenerUrl]
  )

  const handleDesvincular = useCallback(
    async (documento: Documento) => {
      if (!puedeQuitar(documento)) return
      try {
        await desvincularDocumento.mutateAsync({
          documentoId: documento.id,
          entidadTipo: 'tarea',
          entidadId: tareaId,
        })
        toast.success('Archivo desvinculado')
      } catch (error) {
        console.error('Error al desvincular:', error)
        toast.error('Error al desvincular archivo')
      }
    },
    [tareaId, desvincularDocumento, puedeQuitar]
  )

  if (isLoading) {
    return (
      <div className={cn('flex justify-center py-2', className)}>
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className={className}>
      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <Paperclip className="h-4 w-4" />
        <h4 className="text-sm font-medium flex-1">
          Archivos adjuntos {documentos.length > 0 && `(${documentos.length})`}
        </h4>
      </div>

      {/* Input oculto para subir archivos */}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={handleFileChange}
        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
      />

      {/* Lista de archivos */}
      <div className="space-y-2 mb-3">
        {documentos.map((documento) => (
          <div
            key={documento.id}
            className="group flex items-center gap-2 p-2 rounded-lg border bg-background hover:bg-muted/50 transition-colors"
          >
            {getDocumentoIcon(documento.mime_type)}

            <div className="flex-1 min-w-0">
              <p className="text-sm truncate">{documento.nombre}</p>
              <p className="text-xs text-muted-foreground">
                {formatFileSize(documento.archivo_size)}
              </p>
            </div>

            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 text-muted-foreground hover:text-foreground"
                onClick={() => handleVer(documento)}
                title="Ver"
              >
                <Eye className="h-3 w-3" />
              </Button>

              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 text-muted-foreground hover:text-foreground"
                onClick={() => handleDescargar(documento)}
                title="Descargar"
              >
                <Download className="h-3 w-3" />
              </Button>

              {puedeQuitar(documento) && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-muted-foreground hover:text-destructive"
                  onClick={() => handleDesvincular(documento)}
                  title="Quitar"
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>
        ))}

        {documentos.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-2">
            No hay archivos adjuntos
          </p>
        )}
      </div>

      {/* Boton para agregar */}
      {puedeSubir && (
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start text-muted-foreground"
          onClick={handleClickSubir}
          disabled={isUploading}
        >
          {isUploading ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Plus className="h-4 w-4 mr-2" />
          )}
          {isUploading ? 'Subiendo...' : 'Adjuntar archivo'}
        </Button>
      )}
    </div>
  )
}
